import { ClientResponseError } from "pocketbase";
import i18n from "@/i18n";

type FieldError = { code?: string; message?: string };

export type SetFieldError = (name: string, error: { type: string; message: string }) => void;

/**
 * Maps a PocketBase validation error onto form fields.
 *
 * PocketBase returns `{ data: { <field>: { code, message } } }` for 400 responses. Every field
 * that exists on the form gets an inline error; the returned string is what is left to show
 * globally (a toast), or `null` if everything was attached to a field.
 */
export function applyServerErrors(
  error: unknown,
  setError: SetFieldError,
  fields?: readonly string[],
): string | null {
  if (!(error instanceof ClientResponseError)) {
    return error instanceof Error ? error.message : "Something went wrong";
  }

  const data = (error.response?.data ?? {}) as Record<string, FieldError>;
  let attached = 0;
  for (const [name, fieldError] of Object.entries(data)) {
    if (fields && !fields.includes(name)) continue;
    setError(name, { type: "server", message: fieldMessage(name, fieldError) });
    attached += 1;
  }

  if (attached > 0) return null;
  return error.response?.message || error.message || "Request failed";
}

/**
 * PocketBase writes its validation messages in English ("Value must be unique."). Known codes get
 * a sentence in the person's language, per field where the field changes what helps (QA-0004).
 */
function fieldMessage(name: string, fieldError: FieldError | undefined): string {
  const fallback = fieldError?.message || "Invalid value";
  const code = fieldError?.code;
  if (!code) return fallback;
  for (const key of [`serverErrors.${name}.${code}`, `serverErrors.${code}`]) {
    if (i18n.exists(key)) return i18n.t(key as never);
  }
  return fallback;
}
